// Pasos guiados (ADR 0004 §8): label → challenge → views (4) → ruler → declarations → summary.
// El paso `permission` (cámara + guardián + sesión) lo lleva `KypCapture` antes de crear esto.
// Las comprobaciones de calidad solo avisan y dejan repetir: nunca «validan».
import { deriveBurst } from './burst.js';
import { buildShot, normalizeDeclarations } from './bundle.js';
import { ChallengeRunner, type MotionSource } from './ChallengeRunner.js';
import type { CaptureClock } from './clock.js';
import type { Burst, FrameRing } from './FrameRing.js';
import {
  VIEWS,
  type CaptureUI,
  type QualityWarning,
  type UIAction,
  type ViewName,
} from './screens.js';
import type { CaptureSigner } from './SessionKeys.js';
import type {
  CaptureChallengeEvent,
  CaptureDeclarations,
  CaptureSession,
  CaptureShot,
  CaptureStep,
  CaptureVideo,
  CaptureVideoKind,
} from './types.js';
import type { Uploader } from './Uploader.js';
import type { VideoRecorder } from './VideoRecorder.js';

/** Orden de los pasos y porcentaje al empezar cada uno. */
export const STEP_PERCENT: Record<CaptureStep, number> = {
  permission: 0,
  label: 10,
  challenge: 25,
  views: 45,
  ruler: 80,
  declarations: 88,
  summary: 95,
};

/** Máximo de disparos por sesión (contrato: índice 0..11). */
export const MAX_SHOTS = 12;

/** Umbrales de la ayuda de calidad (sobre el gris 32×32 del fotograma elegido). */
export const QUALITY = { minLuma: 40, minSd: 6, minSharpness: 12 } as const;

/** Aviso de calidad de un gris 32×32: oscura por luminancia, movida por varianza. */
export function assessQuality(gray32: Uint8Array, luma: number): QualityWarning | undefined {
  if (luma < QUALITY.minLuma) return 'dark';
  let mean = 0;
  for (const g of gray32) mean += g;
  mean /= gray32.length;
  let v = 0;
  for (const g of gray32) v += (g - mean) ** 2;
  const sd = Math.sqrt(v / gray32.length);
  // Varianza del laplaciano (4 vecinos): poca energía de bordes = foto movida o desenfocada.
  let lap = 0;
  let n = 0;
  for (let y = 1; y < 31; y++)
    for (let x = 1; x < 31; x++) {
      const p = y * 32 + x;
      const l =
        4 * gray32[p]! - gray32[p - 1]! - gray32[p + 1]! - gray32[p - 32]! - gray32[p + 32]!;
      lap += l * l;
      n++;
    }
  if (sd < QUALITY.minSd || lap / n < QUALITY.minSharpness) return 'blurry';
  return undefined;
}

export interface GuidedContext {
  ui: CaptureUI;
  ring: FrameRing;
  session: CaptureSession;
  signer: CaptureSigner;
  clock: CaptureClock;
  uploader: Uploader;
  /** Próxima acción del usuario (con `autoAdvance` en pruebas). */
  next: () => Promise<UIAction>;
  /** Crea el grabador de un vídeo (sube cada trozo al cerrarse). */
  newRecorder: (kind: CaptureVideoKind) => VideoRecorder;
  /** Aviso de cambio de paso (eventos `step`/`progress` y `POST …/progress`). */
  onStep: (step: CaptureStep, extra?: { view?: ViewName }) => void;
  /** Lector de códigos de barras sobre la vista previa, si el navegador lo tiene. */
  detectBarcode?: (() => Promise<string | null>) | null;
  motion?: MotionSource | null;
  /** Duración objetivo del calendario de luz (ms) para `fastChallenge`. */
  challengeTargetMs?: number;
  rulerUrl?: string;
  sleep: (ms: number) => Promise<void>;
}

/** Qué muestra un disparo: la etiqueta o una de las cuatro vistas. */
export type ShotKind = 'label' | ViewName;

/** Lo capturado, listo para montar el bundle. */
export interface GuidedOutcome {
  shots: CaptureShot[];
  /** Qué se fotografió en cada disparo (mismo orden que `shots`). */
  shotKinds: ShotKind[];
  /** Binarios aceptados por hash (fotogramas y trozos). */
  binaries: Map<string, Uint8Array>;
  videos: CaptureVideo[];
  events: CaptureChallengeEvent[];
  declarations: CaptureDeclarations;
}

/**
 * Guía de captura.
 *
 * ```ts
 * const outcome = await new GuidedCapture(ctx).run(); // hasta que el usuario pulsa «Firmar y enviar»
 * ```
 */
export class GuidedCapture {
  private nextIndex = 0;
  private readonly shots: CaptureShot[] = [];
  private readonly shotKinds: ShotKind[] = [];
  private readonly binaries = new Map<string, Uint8Array>();
  private tracking: string | undefined;

  constructor(private readonly ctx: GuidedContext) {}

  async run(): Promise<GuidedOutcome> {
    await this.label();
    const { video, events } = await this.challenge();
    for (const [k, view] of VIEWS.entries()) await this.view(view, k + 1);
    const dims = await this.ruler();
    const declared = await this.declarations();
    const videos = video ? [video] : [];
    this.ctx.onStep('summary');
    const declarations = normalizeDeclarations({ ...dims, ...declared });
    for (;;) {
      this.ctx.ui.render({
        kind: 'summary',
        shots: this.shots.length,
        chunks: video?.chunks.length ?? 0,
        phase: 'ready',
      });
      if ((await this.ctx.next()).type === 'primary') break;
    }
    return {
      shots: this.shots,
      shotKinds: this.shotKinds,
      binaries: this.binaries,
      videos,
      events,
      declarations,
    };
  }

  /** Etiqueta: BarcodeDetector si existe (rellena el tracking), si no campo manual. */
  private async label(): Promise<void> {
    const { ui } = this.ctx;
    this.ctx.onStep('label');
    ui.render({ kind: 'label', busy: false });
    let stop = false;
    const scan = async () => {
      const detect = this.ctx.detectBarcode;
      while (detect && !stop && !this.tracking) {
        const code = await detect().catch(() => null);
        if (code && !stop) {
          this.tracking = code.slice(0, 200);
          ui.fill?.('tracking', this.tracking);
          ui.render({
            kind: 'label',
            busy: false,
            detected: this.tracking,
            tracking: this.tracking,
          });
        }
        if (!this.tracking) await this.ctx.sleep(500);
      }
    };
    const scanning = scan();
    try {
      for (;;) {
        const action = await this.ctx.next();
        if (action.type !== 'primary') continue;
        const typed = action.values?.tracking?.trim();
        if (typed) this.tracking = typed.slice(0, 200);
        ui.render({
          kind: 'label',
          busy: true,
          ...(this.tracking ? { tracking: this.tracking } : {}),
        });
        const shown = (warning?: QualityWarning) =>
          ui.render({
            kind: 'label',
            busy: false,
            ...(this.tracking ? { tracking: this.tracking } : {}),
            ...(warning ? { warning } : {}),
          });
        if (await this.shootAndAccept('label', shown)) break;
      }
    } finally {
      stop = true;
      await scanning;
    }
  }

  /** Reto de luz y movimiento con su vídeo. */
  private async challenge(): Promise<{
    video: CaptureVideo | null;
    events: CaptureChallengeEvent[];
  }> {
    const { ui, session } = this.ctx;
    this.ctx.onStep('challenge');
    ui.render({ kind: 'challenge', phase: 'intro' });
    while ((await this.ctx.next()).type !== 'primary');
    ui.render({ kind: 'challenge', phase: 'running' });
    const recorder = this.ctx.newRecorder('challenge');
    const outcome = await new ChallengeRunner({
      schedule: session.challenge,
      clock: this.ctx.clock,
      display: ui.challengeDisplay(),
      frames: (l) => this.ctx.ring.onFrame(l),
      recorder,
      ...(this.ctx.challengeTargetMs ? { targetMs: this.ctx.challengeTargetMs } : {}),
      motion: this.ctx.motion ?? null,
      sleep: this.ctx.sleep,
    }).run();
    for (const c of recorder.recorded) this.binaries.set(c.meta.sha256, c.bytes);
    ui.render({ kind: 'challenge', phase: 'done' });
    return outcome;
  }

  /** Una vista: disparo, ayuda de calidad (repetir o usar igualmente). */
  private async view(view: ViewName, n: number): Promise<void> {
    const { ui } = this.ctx;
    this.ctx.onStep('views', { view });
    const screen = { kind: 'view' as const, view, n, total: VIEWS.length };
    ui.render({ ...screen, busy: false });
    for (;;) {
      const action = await this.ctx.next();
      if (action.type !== 'primary') continue;
      ui.render({ ...screen, busy: true });
      const accepted = await this.shootAndAccept(view, (warning) =>
        ui.render({ ...screen, busy: false, ...(warning ? { warning } : {}) }),
      );
      if (accepted) return;
    }
  }

  /**
   * Dispara, firma y pregunta si hay aviso de calidad. Devuelve `true` si el disparo se acepta
   * (sus binarios se encolan para subir); con «Repetir» se descarta y se dispara de nuevo.
   */
  private async shootAndAccept(
    kind: ShotKind,
    show: (warning?: QualityWarning) => void,
  ): Promise<boolean> {
    const { shot, burst } = await this.shoot();
    const chosen = burst.frames[burst.chosen_frame]!.source;
    const warning = assessQuality(chosen.gray32, chosen.luma);
    if (warning && this.nextIndex < MAX_SHOTS) {
      show(warning);
      for (;;) {
        const a = await this.ctx.next();
        if (a.type === 'retry') {
          show();
          return false;
        }
        if (a.type === 'primary') break;
      }
    }
    this.accept(kind, shot, burst);
    show();
    return true;
  }

  private async shoot(): Promise<{ shot: CaptureShot; burst: Burst }> {
    if (this.nextIndex >= MAX_SHOTS) throw new Error('max_shots_reached');
    const index = this.nextIndex++;
    const { session, signer, ring } = this.ctx;
    const burst = await ring.shoot(await deriveBurst(session.seed, index));
    const shot = await buildShot(session.id, session.server_nonce, index, burst, signer);
    return { shot, burst };
  }

  private accept(kind: ShotKind, shot: CaptureShot, burst: Burst): void {
    this.shots.push(shot);
    this.shotKinds.push(kind);
    for (const f of burst.frames) {
      this.binaries.set(f.meta.sha256, f.bytes);
      void this.ctx.uploader.enqueue(f.bytes, f.meta.sha256);
    }
  }

  /** Regla y medidas estimadas (opcional). */
  private async ruler(): Promise<Record<string, string>> {
    this.ctx.onStep('ruler');
    this.ctx.ui.render({
      kind: 'ruler',
      ...(this.ctx.rulerUrl ? { rulerUrl: this.ctx.rulerUrl } : {}),
    });
    const a = await this.ctx.next();
    return a.type === 'primary' ? (a.values ?? {}) : {};
  }

  /** Peso, contenido y tracking tecleados (declaraciones del usuario). */
  private async declarations(): Promise<Record<string, string>> {
    this.ctx.onStep('declarations');
    this.ctx.ui.render({
      kind: 'declarations',
      ...(this.tracking ? { tracking: this.tracking } : {}),
    });
    const a = await this.ctx.next();
    const values = a.type === 'primary' ? (a.values ?? {}) : {};
    return { ...(this.tracking ? { tracking: this.tracking } : {}), ...values };
  }
}
